/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

import EventEmitter from 'eventemitter3';
import isObject from 'lodash/isObject';

import ScheduleRegion from './schedule-region';

/**
 * Strategie přehrávání pro vrstvu šablony.
 * @implements {ScheduleStrategy}
 */
class ScheduleLayer {
  /**
   * Vytvoří instanci.
   * @param {ContentInfo} contentInfo Informace o obsahu zařízení.
   * @param {Layer} layer Vrstva, kterou strategie přehrává.
   * @param {ViewerRegistry} viewerRegistry Registr informací o typech médií.
   * @returns {ScheduleLayer} Nová instance.
   */
  constructor(contentInfo, layer, viewerRegistry) {
    if (!isObject(layer)) {
      throw new TypeError(`Invalid layer ${layer}.`);
    }
    this.emitter = new EventEmitter();
    this.layer = layer;
    this.layout = layer.parentLayout;
    const scheduleStrategy = ScheduleLayer.detectStrategy(
      contentInfo,
      layer,
      viewerRegistry
    );
    this.scheduleStrategy = scheduleStrategy;
    // Navázání naslouchání na instanci.
    this.onGone = this.onGone.bind(this);
    this.onPlayed = this.onPlayed.bind(this);
    // Naslouchání událostem.
    scheduleStrategy.on('gone', this.onGone);
    scheduleStrategy.on('played', this.onPlayed);
  }

  /** @inheritdoc */
  destroy() {
    const { scheduleStrategy } = this;
    scheduleStrategy.off('gone', this.onGone);
    scheduleStrategy.off('played', this.onPlayed);
    scheduleStrategy.destroy();
  }

  /**
   * Detekuje strategii pro vrstvu a vytvoří její instanci.
   * @param {ContentInfo} contentInfo Informace o obsahu zařízení.
   * @param {Layer} layer Vrstva šablony.
   * @param {ViewerRegistry} viewerRegistry Registr informací o typech médií.
   * @private
   * @returns {ScheduleStrategy} Detekovaná strategie plánování.
   */
  static detectStrategy(contentInfo, layer, viewerRegistry) {
    // Jelikož je layer region, delegujeme na něho detekci strategie,
    // což zajistí zaobalení regionem, "sirotčinec" a další věci.
    // TODO #458 Vnitřní šablony nyní neumíme (a tedy Layer až na položku layout de facto
    // implementuje rozhraní Content), ale ScheduleContent.detectStrategy to ošetřuje.
    /** @type {Content} */
    const content = layer;
    return ScheduleRegion.fromContent(content, contentInfo, viewerRegistry);
  }

  /**
   * Určí informace pro přehrání média.
   * @param {ScheduleInfo} scheduleInfo Informace z podřízené strategie.
   * @private
   * @returns {ScheduleInfo} Informace pro přehrání média.
   */
  getInfo(scheduleInfo) {
    return scheduleInfo.merge({
      layer: this.layer,
      layout: this.layout
    });
  }

  /** @inheritdoc */
  go() {
    const scheduleInfo = this.scheduleStrategy.go();
    return this.getInfo(scheduleInfo);
  }

  /** @inheritdoc */
  off(...args) {
    this.emitter.off(...args);
  }

  /** @inheritdoc */
  on(...args) {
    this.emitter.on(...args);
  }

  /**
   * Reakce na přechod na další slajd.
   * @param {ScheduleInfo} scheduleInfo Informace potřebné k přehrání slajdu, na které byl proveden přechod.
   * @private
   * @returns {undefined} Nic.
   */
  onGone(scheduleInfo) {
    this.emitter.emit('gone', this.getInfo(scheduleInfo));
  }

  /**
   * Reakce na přehrání slajdu.
   * @param {ScheduleInfo} scheduleInfo Informace o slajdu, který byl přehrán.
   * @private
   * @returns {undefined} Nic.
   */
  onPlayed(scheduleInfo) {
    this.emitter.emit('played', this.getInfo(scheduleInfo));
  }

  /** @inheritdoc */
  plan(offset) {
    return this.scheduleStrategy
      .plan(offset)
      .map((scheduleInfo) => this.getInfo(scheduleInfo));
  }

  /** @inheritdoc */
  skip() {
    this.scheduleStrategy.skip();
  }

  /** @inheritdoc */
  stop() {
    this.scheduleStrategy.stop();
  }

  /** @inheritdoc */
  toString() {
    return `ScheduleLayer {layer: ${this.layer}}`;
  }
}

export default ScheduleLayer;
